/**
 * Componente de Gráfico de Evolução
 * Exibe a evolução de consultas e usuários nos últimos 30 dias
 */

import React from 'react';
import {
  Box,
  Card,
  CardBody,
  Heading,
  Text,
  useColorModeValue,
  VStack,
  HStack,
  Badge,
} from '@chakra-ui/react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { EvolucaoData } from '../../services/dashboard';

interface EvolucaoChartProps {
  data: EvolucaoData[];
  loading?: boolean;
}

export const EvolucaoChart: React.FC<EvolucaoChartProps> = ({ data, loading }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Formatar data para exibição
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit' 
    });
  };

  // Tooltip customizado
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          bg={cardBg}
          p={3}
          borderRadius="md"
          boxShadow="lg"
          border="1px solid"
          borderColor={borderColor}
        >
          <Text fontWeight="bold" mb={2}>
            {formatDate(label)}
          </Text>
          {payload.map((entry: any, index: number) => (
            <HStack key={index} spacing={2}>
              <Box
                w={3}
                h={3}
                borderRadius="full"
                bg={entry.color}
              />
              <Text fontSize="sm">
                {entry.name}: {entry.value}
              </Text>
            </HStack>
          ))}
        </Box>
      );
    }
    return null;
  };

  // Calcular totais
  const totalConsultas = data.reduce((sum, item) => sum + item.consultas, 0);
  const totalUsuarios = data.reduce((sum, item) => sum + item.usuarios, 0);

  if (loading) {
    return (
      <Card bg={cardBg}>
        <CardBody>
          <VStack spacing={4} align="center" justify="center" minH="300px">
            <Text>Carregando dados de evolução...</Text>
          </VStack>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card bg={cardBg}>
      <CardBody>
        <VStack align="stretch" spacing={4}>
          {/* Header */}
          <HStack justify="space-between" align="center">
            <Heading size="md">Evolução dos Últimos 30 Dias</Heading>
            <HStack spacing={2}>
              <Badge colorScheme="blue" variant="subtle">
                {totalConsultas} consultas
              </Badge>
              <Badge colorScheme="green" variant="subtle">
                {totalUsuarios} novos usuários
              </Badge>
            </HStack>
          </HStack>

          {/* Gráfico */}
          <Box h="300px" w="100%">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis 
                  dataKey="data" 
                  tickFormatter={formatDate}
                  fontSize={12}
                  tick={{ fill: textColor }}
                />
                <YAxis 
                  fontSize={12}
                  tick={{ fill: textColor }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="consultas"
                  stroke="#3182CE"
                  strokeWidth={2}
                  dot={{ fill: '#3182CE', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Consultas"
                />
                <Line
                  type="monotone"
                  dataKey="usuarios"
                  stroke="#38A169"
                  strokeWidth={2}
                  dot={{ fill: '#38A169', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Novos Usuários"
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {/* Resumo */}
          <HStack justify="space-around" pt={2}>
            <VStack spacing={1}>
              <Text fontSize="sm" color={textColor}>
                Média de Consultas/Dia
              </Text>
              <Text fontWeight="bold" fontSize="lg">
                {(totalConsultas / 30).toFixed(1)}
              </Text>
            </VStack>
            <VStack spacing={1}>
              <Text fontSize="sm" color={textColor}>
                Média de Usuários/Dia
              </Text>
              <Text fontWeight="bold" fontSize="lg">
                {(totalUsuarios / 30).toFixed(1)}
              </Text>
            </VStack>
          </HStack>
        </VStack>
      </CardBody>
    </Card>
  );
};

export default EvolucaoChart;